import type { CustomerContact } from '@clockwise/clients/customer-contacts';
import { ApiError, formatPhone } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { RowActions } from '@/components/custom/row-actions';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { customerContactsApi } from '@/lib/api';
import { CONTACT_TYPE_LABELS, contactName } from '@/lib/customer-contact-display';
import { isForbidden } from '@/lib/errors';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { CustomerContactFormSheet } from './customer-contact-form-sheet';
import { CustomerContactViewSheet } from './customer-contact-view-sheet';
import { RequirePermission } from './require-permission';
import { SHEET_SWAP_DELAY_MS } from './sheet-swap';

interface Props {
  customerId: string;
}

// One modal sheet at a time: add/edit drive the form sheet, view drives the
// read-only sheet. A single union (rather than parallel open/target booleans)
// keeps "which sheet, which row" unambiguous and means only one Radix modal is
// ever open.
type SheetState =
  | { mode: 'add' }
  | { mode: 'edit'; contact: CustomerContact }
  | { mode: 'view'; contact: CustomerContact }
  | null;

export function CustomerContactsPanel({ customerId }: Props) {
  const queryClient = useQueryClient();
  const canView = useHasPermission('customer.contact.view');
  const canEdit = useHasPermission('customer.contact.edit');

  const [sheet, setSheet] = useState<SheetState>(null);

  const contactsQuery = useQuery({
    queryKey: queryKeys.customerContacts.list(customerId),
    queryFn: () => customerContactsApi.list(customerId),
  });
  const contacts = contactsQuery.data?.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customerContactsApi.delete(id),
    onSuccess: () => {
      toast.success('Contact removed');
      queryClient.invalidateQueries({ queryKey: queryKeys.customerContacts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to remove contact'),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="ml-auto">
          <RequirePermission permission="customer.contact.edit">
            <Button size="sm" onClick={() => setSheet({ mode: 'add' })}>
              <Plus />
              Add contact
            </Button>
          </RequirePermission>
        </div>
      </div>

      <div className="rounded-md border">
        {contactsQuery.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : contactsQuery.isError ? (
          isForbidden(contactsQuery.error) ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              You don’t have access to this customer’s contacts.
            </p>
          ) : (
            <p className="p-4 text-sm text-destructive">
              {contactsQuery.error instanceof ApiError
                ? contactsQuery.error.message
                : 'Failed to load contacts.'}
            </p>
          )
        ) : contacts.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            No contacts yet. Click “Add contact”.
          </p>
        ) : (
          <Table>
            {/* `bg-surface-subtle` — the same cream header tint every other
                list table uses (see `sticky-table-scroll.tsx`); this table
                isn't wrapped in that component (it scrolls with the page,
                not internally), so the tint has to be stated here instead. */}
            <TableHeader className="bg-surface-subtle">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="w-24 text-right" aria-label="actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((c) => (
                <TableRow
                  key={c.id}
                  className={canView ? 'cursor-pointer' : undefined}
                  onClick={canView ? () => setSheet({ mode: 'view', contact: c }) : undefined}
                >
                  <TableCell>
                    <span className="font-medium">{contactName(c)}</span>
                    {c.primary ? (
                      <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">
                        Primary
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm">{c.title || '—'}</TableCell>
                  <TableCell className="text-sm">
                    {c.email ? (
                      <a
                        className="underline-offset-2 hover:underline"
                        href={`mailto:${c.email}`}
                        // Don't let following the email also open the view sheet.
                        onClick={(e) => e.stopPropagation()}
                      >
                        {c.email}
                      </a>
                    ) : (
                      '—'
                    )}
                    {c.ccEmails.length > 0 ? (
                      <span
                        className="ml-2 text-xs text-muted-foreground"
                        title={c.ccEmails.join(', ')}
                      >
                        +{c.ccEmails.length} CC
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatPhone(c.workPhone || c.mobile) || '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {c.contactType ? CONTACT_TYPE_LABELS[c.contactType] : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    {canEdit ? (
                      <RowActions>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Edit"
                          onClick={(e) => {
                            // Don't let the click bubble to the row, which would
                            // open the read-only view sheet.
                            e.stopPropagation();
                            setSheet({ mode: 'edit', contact: c });
                          }}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Remove contact"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`Remove ${contactName(c)}?`)) {
                              deleteMutation.mutate(c.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 />
                        </Button>
                      </RowActions>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <CustomerContactFormSheet
        open={sheet?.mode === 'add' || sheet?.mode === 'edit'}
        onOpenChange={(o) => {
          if (!o) setSheet(null);
        }}
        customerId={customerId}
        contact={sheet?.mode === 'edit' ? sheet.contact : null}
      />

      <CustomerContactViewSheet
        open={sheet?.mode === 'view'}
        onOpenChange={(o) => {
          if (!o) setSheet(null);
        }}
        contact={sheet?.mode === 'view' ? sheet.contact : null}
        onEdit={() => {
          if (sheet?.mode !== 'view') return;
          // Close the view sheet, then open the edit form only after its modal
          // overlay tears down — two Radix modal dialogs transitioning in the
          // same tick can leave the page inert (body pointer-events: none).
          const { contact } = sheet;
          setSheet(null);
          window.setTimeout(() => setSheet({ mode: 'edit', contact }), SHEET_SWAP_DELAY_MS);
        }}
      />
    </div>
  );
}
